import DOMPurify from 'dompurify';

/** Browser "Copy image" can supply HTML as well as bitmap data. */
export function standaloneClipboardImage(html: string): HTMLImageElement | null {
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    const image = body.querySelector('img[src]');
    if (!image || body.querySelectorAll('img').length !== 1 || body.textContent?.trim()) return null;
    // Allow insignificant wrappers, but preserve actual rich structures such as tables/lists.
    if (Array.from(body.querySelectorAll('*')).some(element =>
        !['IMG', 'DIV', 'SPAN', 'P', 'A', 'BR'].includes(element.tagName))) return null;
    return image;
}

/** Keep SVG graphics self-contained and inert inside an image node. */
export function svgImageSource(element: HTMLElement): string {
    const svg = element.cloneNode(true) as Element;
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const clean = DOMPurify.sanitize(svg.outerHTML, {
        USE_PROFILES: { svg: true, svgFilters: true },
        FORBID_TAGS: ['script', 'foreignObject'],
    });
    // Encode UTF-8 before base64 so non-ASCII SVG titles/text survive.
    const bytes = encodeURIComponent(clean).replace(/%([0-9A-F]{2})/g,
        (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)));
    return `data:image/svg+xml;base64,${btoa(bytes)}`;
}

export function imageElement(element: HTMLElement): Element | null {
    return element.tagName.toLowerCase() === 'img' ? element : element.querySelector('img');
}

export function imageDimension(element: HTMLElement, dimension: 'width' | 'height'): number | null {
    const image = element.tagName.toLowerCase() === 'svg' ? element : imageElement(element);
    const attribute = image?.getAttribute(dimension)?.trim();
    // HTML img dimensions are integer pixel counts. Old pasted content may
    // contain fractional values from the former em-to-pixel parsing bug.
    const attributePattern = element.tagName.toLowerCase() === 'svg'
        ? /^(\d+(?:\.\d+)?|\.\d+)(?:px)?$/i : /^(\d+)(?:px)?$/i;
    const attributeMatch = attribute?.match(attributePattern);
    if (attributeMatch && Number(attributeMatch[1]) > 0) return Math.max(1, Math.round(Number(attributeMatch[1])));
    const value = (image as HTMLElement | null)?.style[dimension];
    const match = value?.trim().match(/^(\d+(?:\.\d+)?|\.\d+)(px|em|rem|pt)?$/i);
    if (!match) return null;
    const rootSize = parseFloat(element.ownerDocument.defaultView?.getComputedStyle(element.ownerDocument.documentElement).fontSize || '') || 16;
    let fontSize = rootSize;
    const ancestors: Element[] = [];
    for (let ancestor = image; ancestor; ancestor = ancestor.parentElement) ancestors.unshift(ancestor);
    for (const ancestor of ancestors) {
        const font = (ancestor as HTMLElement).style?.fontSize.match(/^(\d+(?:\.\d+)?)(px|em|rem|pt|%)$/i);
        if (!font) continue;
        const unit = font[2].toLowerCase();
        fontSize = Number(font[1]) * (unit === 'em' ? fontSize : unit === 'rem' ? rootSize : unit === 'pt' ? 96 / 72 : unit === '%' ? fontSize / 100 : 1);
    }
    const unit = match[2]?.toLowerCase();
    const pixels = Number(match[1]) * (unit === 'em' ? fontSize : unit === 'rem' ? rootSize : unit === 'pt' ? 96 / 72 : 1);
    return Number.isFinite(pixels) && pixels > 0 ? Math.max(1, Math.round(pixels)) : null;
}
